<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="pageTitle">
          <template v-slot:preview>
            <b-form>
              <!-- ===================== THÔNG TIN CHUNG ===================== -->
              <b-row class="mb-4">
                <b-col md="5">
                  <b-form-group>
                    <label>Tên chương trình <span class="text-danger">*</span></label>
                    <b-form-input
                      size="sm"
                      v-model="model.name"
                      placeholder="Nhập tên chương trình AdHoc"
                      autocomplete="off"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="2">
                  <b-form-group>
                    <label>Từ ngày <span class="text-danger">*</span></label>
                    <date-picker
                      placeholder="Từ ngày"
                      class="form-control form-control-sm"
                      :config="dpConfigs.date"
                      v-model="model.startDate"
                    ></date-picker>
                  </b-form-group>
                </b-col>
                <b-col md="2">
                  <b-form-group>
                    <label>Đến ngày</label>
                    <date-picker
                      placeholder="Không giới hạn"
                      class="form-control form-control-sm"
                      :config="dpConfigs.date"
                      v-model="model.endDate"
                    ></date-picker>
                  </b-form-group>
                </b-col>
                <b-col md="3">
                  <b-form-group>
                    <label>Trạng thái</label>
                    <b-form-select
                      size="sm"
                      v-model="model.status"
                      :options="statusOptions"
                      value-field="id"
                      text-field="name"
                    ></b-form-select>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row class="mb-4">
                <b-col md="12">
                  <b-form-group>
                    <label>Mô tả</label>
                    <b-form-textarea
                      size="sm"
                      rows="2"
                      v-model="model.description"
                      placeholder="Mô tả chương trình"
                    ></b-form-textarea>
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- ============ PHẠM VI: NHIỀU CH BÁN LẺ + BÁN BUÔN ============ -->
              <div class="section-title">Phạm vi áp dụng</div>
              <b-row class="mb-4">
                <b-col md="6">
                  <b-form-group>
                    <label>Cửa hàng bán lẻ</label>
                    <b-form-checkbox
                      class="mb-2"
                      v-model="model.applyAllRetail"
                      @change="onToggleApplyAllRetail"
                    >
                      Áp dụng toàn bộ cửa hàng bán lẻ
                    </b-form-checkbox>
                    <vue-multi-select
                      v-if="!model.applyAllRetail"
                      v-model="model.retailStores"
                      :options="stores"
                      placeholder="Chọn cửa hàng bán lẻ"
                      option-label="name"
                    ></vue-multi-select>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group>
                    <label>Cửa hàng bán buôn</label>
                    <b-form-checkbox
                      class="mb-2"
                      v-model="model.applyAllWholesale"
                      @change="onToggleApplyAllWholesale"
                    >
                      Áp dụng toàn bộ cửa hàng bán buôn
                    </b-form-checkbox>
                    <vue-multi-select
                      v-if="!model.applyAllWholesale"
                      v-model="model.wholesaleStores"
                      :options="stores"
                      placeholder="Chọn cửa hàng bán buôn"
                      option-label="name"
                    ></vue-multi-select>
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- ==================== CHI TIẾT THƯỞNG ==================== -->
              <div class="section-title d-flex justify-content-between align-items-center">
                <span>Chi tiết thưởng</span>
                <b-button variant="primary" size="sm" @click="openDetailModal">
                  <i class="flaticon2-add-1 mr-1"></i>Thêm
                </b-button>
              </div>

              <b-alert show variant="light" class="mb-3 detail-hint">
                Mỗi dòng đọc là: <strong>bán sản phẩm/danh mục này</strong>, doanh thu
                đạt ngưỡng cấu hình, <strong>và</strong> hoá đơn có kèm sản phẩm thuộc
                nhóm điều kiện <strong>thì</strong> mỗi vị trí được số tiền tương ứng.
              </b-alert>

              <b-table
                v-if="model.details.length"
                class="table-bordered table-hover mb-3"
                :items="model.details"
                :fields="detailFields"
                small
              >
                <template v-slot:cell(count)="row">
                  {{ row.index + 1 }}
                </template>
                <template v-slot:cell(item)="row">
                  <b-badge
                    :variant="
                      row.item.type === ITEM_TYPE.PRODUCT ? 'primary' : 'secondary'
                    "
                    class="mr-1"
                    >{{ itemTypeName(row.item.type) }}</b-badge
                  >
                  {{ row.item.name }}
                </template>
                <template v-slot:cell(condition)="row">
                  <div>{{ describeAmount(row.item) }}</div>
                  <div class="text-muted small mt-1">
                    <b-badge
                      :variant="
                        row.item.rewardMode === REWARD_MODE.PLUS
                          ? 'info'
                          : 'secondary'
                      "
                      >{{ rewardModeName(row.item.rewardMode) }}</b-badge
                    >
                  </div>
                  <div
                    v-if="row.item.conditionCategories.length"
                    class="text-muted small mt-1"
                  >
                    Kèm:
                    {{
                      row.item.conditionCategories
                        .map((c) => c.name)
                        .join(
                          row.item.conditionLogic === CONDITION_LOGIC.AND
                            ? ' VÀ '
                            : ' HOẶC ',
                        )
                    }}
                  </div>
                </template>
                <template v-slot:cell(rates)="row">
                  <span
                    v-for="rate in describeRates(row.item)"
                    :key="rate.jobTitleId"
                    class="badge badge-light mr-1"
                    >{{ rate.name }}: {{ rate.amount }}</span
                  >
                </template>
                <template v-slot:cell(actions)="row">
                  <b-button
                    variant="link"
                    size="sm"
                    class="text-danger p-0"
                    @click="removeDetail(row.index)"
                  >
                    <i class="flaticon2-rubbish-bin-delete-button"></i>
                  </b-button>
                </template>
              </b-table>

              <b-alert v-if="!model.details.length" show variant="secondary">
                Chưa có dòng chi tiết nào. Bấm <strong>Thêm</strong> để bắt đầu.
              </b-alert>

              <!-- ======================== HÀNH ĐỘNG ======================== -->
              <div class="mt-4">
                <b-button
                  variant="primary"
                  size="sm"
                  class="mr-2"
                  :disabled="isSaving"
                  @click="save"
                >
                  <i class="flaticon2-check-mark mr-1"></i>
                  {{ isSaving ? 'Đang lưu...' : 'Lưu' }}
                </b-button>
                <b-button variant="secondary" size="sm" @click="goBack">
                  Huỷ
                </b-button>
              </div>
            </b-form>
          </template>
        </KTCodePreview>
      </div>
    </div>

    <!-- ============== MODAL THÊM DÒNG CHI TIẾT ==============
         Chỉ THÊM, không sửa: dòng đã thêm hiển thị ở bảng chỉ đọc.
         Sai thì xoá dòng rồi thêm lại — tránh sửa nhầm cấu hình đã chốt.

         Bố cục 3 khối theo đúng thứ tự người dùng nghĩ:
           1. Thưởng cho cái gì   2. Với điều kiện nào   3. Ai được bao nhiêu -->
    <b-modal
      v-model="detailModalVisible"
      title="Thêm dòng chi tiết thưởng"
      size="lg"
      @ok="confirmAddDetail"
      @hidden="resetDetailForm"
      ok-title="Thêm"
      cancel-title="Huỷ"
      :ok-disabled="!canAddDetail"
    >
      <!-- ---------- 1. Sản phẩm được thưởng ---------- -->
      <div class="form-block">
        <div class="form-block__title">1. Sản phẩm được thưởng</div>
        <b-row>
          <b-col md="4">
            <label class="form-label">Loại</label>
            <b-form-select
              size="sm"
              v-model="detailForm.type"
              :options="itemTypeOptions"
              value-field="id"
              text-field="name"
              @change="onDetailTypeChange"
            ></b-form-select>
          </b-col>
          <b-col md="8">
            <label class="form-label">
              {{ detailForm.type === ITEM_TYPE.PRODUCT ? 'Sản phẩm' : 'Danh mục' }}
              <span class="text-danger">*</span>
            </label>
            <Autosuggest
              :key="'modal-item-' + componentKey"
              :model="detailForm.name"
              :suggestions="itemSuggestions"
              :placeholder="
                detailForm.type === ITEM_TYPE.PRODUCT
                  ? 'Nhập tên hoặc mã sản phẩm'
                  : 'Nhập tên danh mục'
              "
              :limit="10"
              @select="onSelectItem($event)"
              @change="onSearchItem($event)"
              suggestionName="suggestionName"
            />
          </b-col>
        </b-row>
      </div>

      <!-- ---------- 2. Điều kiện ---------- -->
      <div class="form-block">
        <div class="form-block__title">2. Điều kiện áp dụng</div>

        <label class="form-label">Doanh thu của dòng hàng</label>
        <!-- Một dải liền mạch đọc như bất phương trình: [số] [toán tử] DT [toán tử] [số] -->
        <div class="range-row mb-3">
          <b-form-input
            size="sm"
            class="range-row__amount"
            v-model="detailForm.amount1"
            placeholder="Không giới hạn"
            autocomplete="off"
          ></b-form-input>
          <b-form-select
            size="sm"
            class="range-row__operator"
            v-model="detailForm.amount1Operator"
            :options="operatorOptions"
          ></b-form-select>
          <span class="range-row__label">DT</span>
          <b-form-select
            size="sm"
            class="range-row__operator"
            v-model="detailForm.amount2Operator"
            :options="operatorOptions"
          ></b-form-select>
          <b-form-input
            size="sm"
            class="range-row__amount"
            v-model="detailForm.amount2"
            placeholder="Không giới hạn"
            autocomplete="off"
          ></b-form-input>
        </div>

        <!-- Đọc lại điều kiện bằng tiếng Việt để người nhập tự soát,
             tránh chọn nhầm chiều toán tử (vế trái là amount1 <op> DT). -->
        <div
          class="range-preview mb-3"
          :class="{ 'range-preview--warn': revenuePreview.warn }"
        >
          <i
            class="mr-1"
            :class="revenuePreview.warn ? 'fas fa-exclamation-triangle' : 'fas fa-eye'"
          ></i>
          {{ revenuePreview.text }}
        </div>

        <b-row>
          <b-col md="8">
            <label class="form-label">Nhóm AdHoc kèm theo</label>
            <vue-multi-select
              v-model="detailForm.conditionCategories"
              :options="adhocCategories"
              placeholder="Chọn nhóm điều kiện"
              option-label="name"
            ></vue-multi-select>
          </b-col>
          <b-col md="4">
            <label class="form-label">Cách ghép nhóm</label>
            <b-form-select
              size="sm"
              v-model="detailForm.conditionLogic"
              :options="conditionLogicOptions"
              value-field="id"
              text-field="name"
              :disabled="detailForm.conditionCategories.length < 2"
            ></b-form-select>
          </b-col>
        </b-row>
      </div>

      <!-- ---------- 3. Cách tính khi trùng hoa hồng thường ---------- -->
      <div class="form-block">
        <div class="form-block__title">3. Khi sản phẩm đã có hoa hồng thường</div>
        <b-row>
          <b-col md="6">
            <label class="form-label">Cách tính</label>
            <b-form-select
              size="sm"
              v-model="detailForm.rewardMode"
              :options="rewardModeOptions"
              value-field="id"
              text-field="name"
            ></b-form-select>
          </b-col>
          <b-col md="6">
            <div class="mode-hint">
              <template v-if="detailForm.rewardMode === REWARD_MODE.MAX">
                Chỉ trả <strong>bên nào nhiều hơn</strong>. Ví dụ hoa hồng thường
                30.000đ, AdHoc 100.000đ → nhân viên nhận <strong>100.000đ</strong>.
              </template>
              <template v-else>
                <strong>Cộng dồn</strong> cả hai. Ví dụ hoa hồng thường 30.000đ,
                AdHoc 100.000đ → nhân viên nhận <strong>130.000đ</strong>.
              </template>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- ---------- 4. Mức thưởng ---------- -->
      <div class="form-block form-block--last">
        <div class="form-block__title">
          4. Mức thưởng theo vị trí <span class="text-danger">*</span>
        </div>
        <b-alert v-if="!jobTitles.length" show variant="secondary" class="mb-0 py-2">
          Chưa tải được danh sách vị trí công việc.
        </b-alert>
        <div v-else class="rate-grid">
          <div v-for="job in jobTitles" :key="'mjob-' + job.id" class="rate-cell">
            <label class="rate-cell__label" :for="'rate-' + job.id">
              {{ job.name }}
            </label>
            <b-input-group size="sm" append="đ">
              <b-form-input
                :id="'rate-' + job.id"
                v-model="detailForm.rates[job.id]"
                placeholder="0"
                autocomplete="off"
                class="text-right"
              ></b-form-input>
            </b-input-group>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
/**
 * Tạo / cập nhật chương trình thưởng AdHoc V2 — SRS F-06.
 *
 * Hai điểm chính của màn này:
 *  1. Phạm vi áp dụng chọn được NHIỀU cửa hàng BÁN LẺ và NHIỀU cửa hàng BÁN BUÔN
 *     (hoặc bật cờ áp dụng toàn bộ từng kênh).
 *  2. Mỗi dòng chi tiết cấu hình được ĐIỀU KIỆN: ngưỡng doanh thu + các nhóm AdHoc
 *     kèm theo, ghép theo HOẶC / VÀ.
 *
 * API nhận cửa hàng dưới dạng phẳng [{storeId, scope}] nên hai danh sách bán lẻ /
 * bán buôn được gộp lại lúc build payload.
 */
import KTCodePreview from '@/view/content/CodePreview.vue';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import VueMultiSelect from 'vue-simple-multi-select';
import datePicker from 'vue-bootstrap-datetimepicker';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import debounce from 'debounce';
import moment from 'moment';
import { TIME_TRIGGER } from '@/utils/constants';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import {
  COMMISSION_ADHOC_STATUS,
  COMMISSION_ADHOC_STATUS_NAME,
  COMMISSION_ADHOC_ITEM_TYPE,
  COMMISSION_ADHOC_ITEM_TYPE_NAME,
  COMMISSION_ADHOC_CONDITION_LOGIC,
  COMMISSION_ADHOC_CONDITION_LOGIC_NAME,
  COMMISSION_ADHOC_REWARD_MODE,
  COMMISSION_ADHOC_REWARD_MODE_NAME,
  COMMISSION_STORE_SCOPE,
  COMMISSION_SEARCH_TYPE,
  COMMISSION_OPERATOR_OPTIONS,
  toOptionList,
} from './commission.constants';

const DATE_INPUT_FORMAT = 'DD/MM/YYYY';
const DATE_API_FORMAT = 'YYYY-MM-DD';

export default {
  components: { KTCodePreview, Autosuggest, VueMultiSelect, datePicker },
  data() {
    return {
      ITEM_TYPE: COMMISSION_ADHOC_ITEM_TYPE,
      CONDITION_LOGIC: COMMISSION_ADHOC_CONDITION_LOGIC,
      REWARD_MODE: COMMISSION_ADHOC_REWARD_MODE,
      componentKey: 0,
      isSaving: false,
      adhocId: null,
      model: {
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        status: COMMISSION_ADHOC_STATUS.ACTIVE,
        applyAllRetail: false,
        applyAllWholesale: false,
        retailStores: [],
        wholesaleStores: [],
        details: [],
      },
      stores: [],
      adhocCategories: [],
      jobTitles: [],
      itemSuggestions: [],
      detailModalVisible: false,
      detailForm: this.emptyDetailForm(),
      detailFields: [
        {
          key: 'count',
          label: 'STT',
          thStyle: { fontWeight: 600, color: '#181c32', width: '5%' },
          tdClass: 'text-center',
        },
        {
          key: 'item',
          label: 'Sản phẩm / Danh mục',
          thStyle: { fontWeight: 600, color: '#181c32', width: '30%' },
        },
        {
          key: 'condition',
          label: 'Điều kiện',
          thStyle: { fontWeight: 600, color: '#181c32', width: '30%' },
        },
        {
          key: 'rates',
          label: 'Mức thưởng',
          thStyle: { fontWeight: 600, color: '#181c32', width: '30%' },
        },
        {
          key: 'actions',
          label: '',
          thStyle: { fontWeight: 600, color: '#181c32', width: '5%' },
          tdClass: 'text-center',
        },
      ],
      statusOptions: toOptionList(COMMISSION_ADHOC_STATUS_NAME),
      itemTypeOptions: toOptionList(COMMISSION_ADHOC_ITEM_TYPE_NAME),
      conditionLogicOptions: toOptionList(COMMISSION_ADHOC_CONDITION_LOGIC_NAME),
      rewardModeOptions: toOptionList(COMMISSION_ADHOC_REWARD_MODE_NAME),
      operatorOptions: COMMISSION_OPERATOR_OPTIONS,
      dpConfigs: {
        date: {
          format: DATE_INPUT_FORMAT,
          useCurrent: false,
          showClear: true,
          showClose: true,
          locale: 'vi',
        },
      },
    };
  },
  computed: {
    /** Câu mô tả điều kiện doanh thu đang nhập trong modal (§preview) */
    revenuePreview() {
      return this.describeRevenue(this.detailForm);
    },
    /** Nút Thêm chỉ bật khi đã chọn item và có ít nhất một mức thưởng > 0 */
    canAddDetail() {
      if (!this.detailForm.referenceId) {
        return false;
      }
      return Object.keys(this.detailForm.rates).some(
        (jobTitleId) => this.toAmount(this.detailForm.rates[jobTitleId]) > 0,
      );
    },
    pageTitle() {
      return this.adhocId
        ? 'Cập nhật chương trình thưởng AdHoc'
        : 'Tạo chương trình thưởng AdHoc';
    },
  },
  created() {
    this.adhocId = Number(this.$route.query.id) || null;
    this.fetchStores();
    this.fetchAdhocCategories();
    this.fetchJobTitles();
    if (this.adhocId) {
      this.fetchDetail();
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hoa hồng', route: '/commission/categories' },
      { title: 'Thưởng AdHoc', route: '/commission/adhocs' },
      { title: this.adhocId ? 'Cập nhật' : 'Tạo mới' },
    ]);
  },
  methods: {
    /* ----------------------------- Nạp danh mục ---------------------------- */
    fetchStores() {
      ApiService.setHeader();
      ApiService.query('stores', { params: { limit: 1000, page: 1 } })
        .then(({ data }) => {
          this.stores = (data.data.list_store || []).map((store) => ({
            id: store.id,
            name: `${store.shortName} (${store.name})`,
          }));
        })
        .catch(() => makeToastFaile('Không tải được danh sách cửa hàng!'));
    },
    fetchAdhocCategories() {
      ApiService.setHeader();
      ApiService.query('commission-adhoc-categories', {
        params: { limit: 200, page: 1 },
      })
        .then(({ data }) => {
          this.adhocCategories = (data.data.items || []).map((row) => ({
            id: row.id,
            name: row.name,
          }));
        })
        .catch(() => makeToastFaile('Không tải được nhóm AdHoc!'));
    },
    fetchJobTitles() {
      ApiService.setHeader();
      ApiService.query('commission-job-titles/allowed', {
        params: { commissionType: 1 },
      })
        .then(({ data }) => {
          this.jobTitles = (data.data || []).map((row) => ({
            id: row.jobTitleId || row.id,
            name: row.jobTitleName || row.name,
          }));
        })
        .catch(() => makeToastFaile('Không tải được danh sách vị trí!'));
    },
    fetchDetail() {
      ApiService.setHeader();
      ApiService.query(`commission-adhocs/${this.adhocId}`)
        .then(({ data }) => this.bindModel(data.data))
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được chương trình'),
          );
        });
    },

    /* --------------------------- Đổ dữ liệu vào form ------------------------ */
    bindModel(adhoc) {
      const retail = [];
      const wholesale = [];
      (adhoc.stores || []).forEach((row) => {
        const item = { id: row.storeId, name: row.storeName };
        if (Number(row.scope) === COMMISSION_STORE_SCOPE.RETAIL) {
          retail.push(item);
        } else {
          wholesale.push(item);
        }
      });

      this.model = {
        name: adhoc.name || '',
        description: adhoc.description || '',
        startDate: this.toInputDate(adhoc.startDate),
        endDate: this.toInputDate(adhoc.endDate),
        status: Number(adhoc.status) || COMMISSION_ADHOC_STATUS.ACTIVE,
        applyAllRetail: Number(adhoc.applyAllRetailStores) === 1,
        applyAllWholesale: Number(adhoc.applyAllWholesaleStores) === 1,
        retailStores: retail,
        wholesaleStores: wholesale,
        details: (adhoc.details || []).map((row) => {
          const rates = {};
          (row.employeeRates || []).forEach((rate) => {
            rates[rate.jobTitleId] = rate.amount;
          });
          return {
            type: Number(row.type),
            referenceId: row.referenceId,
            name: row.name || '',
            amount1: row.amount1,
            amount1Operator: Number(row.amount1Operator) || 1,
            amount2: row.amount2,
            amount2Operator: Number(row.amount2Operator) || 1,
            conditionLogic:
              Number(row.conditionLogic) || COMMISSION_ADHOC_CONDITION_LOGIC.OR,
            rewardMode:
              Number(row.rewardMode) || COMMISSION_ADHOC_REWARD_MODE.MAX,
            conditionCategories: (row.conditions || []).map((c) => ({
              id: c.commissionAdhocCategoryId,
              name: c.name,
            })),
            rates,
          };
        }),
      };
      this.componentKey += 1;
    },

    /* ------------------------------ Ngày tháng ----------------------------- */
    toApiDate(value) {
      if (!value) {
        return null;
      }
      const parsed = moment(value, DATE_INPUT_FORMAT, true);
      return parsed.isValid() ? parsed.format(DATE_API_FORMAT) : null;
    },
    toInputDate(value) {
      return value ? moment(value).format(DATE_INPUT_FORMAT) : '';
    },

    /* ------------------------------ Phạm vi CH ----------------------------- */
    onToggleApplyAllRetail(checked) {
      if (checked) {
        this.model.retailStores = [];
      }
    },
    onToggleApplyAllWholesale(checked) {
      if (checked) {
        this.model.wholesaleStores = [];
      }
    },

    /* ---------------------------- Dòng chi tiết ---------------------------- */
    /** Form rỗng cho modal — dùng cả lúc khởi tạo data lẫn lúc reset */
    emptyDetailForm() {
      return {
        type: COMMISSION_ADHOC_ITEM_TYPE.PRODUCT,
        referenceId: null,
        name: '',
        amount1: '',
        amount1Operator: 1,
        amount2: '',
        amount2Operator: 1,
        conditionLogic: COMMISSION_ADHOC_CONDITION_LOGIC.OR,
        rewardMode: COMMISSION_ADHOC_REWARD_MODE.MAX,
        conditionCategories: [],
        rates: {},
      };
    },
    openDetailModal() {
      this.resetDetailForm();
      this.detailModalVisible = true;
    },
    resetDetailForm() {
      this.detailForm = this.emptyDetailForm();
      this.itemSuggestions = [];
      this.componentKey += 1;
    },
    /** Chốt dòng: đẩy vào danh sách, dòng đã thêm KHÔNG sửa lại được */
    confirmAddDetail() {
      this.model.details.push({
        ...this.detailForm,
        conditionCategories: [...this.detailForm.conditionCategories],
        rates: { ...this.detailForm.rates },
      });
    },
    removeDetail(index) {
      this.model.details.splice(index, 1);
    },
    onDetailTypeChange() {
      // Đổi loại thì item cũ không còn ý nghĩa -> xoá để tránh gửi nhầm id
      this.detailForm.referenceId = null;
      this.detailForm.name = '';
      this.itemSuggestions = [];
      this.componentKey += 1;
    },

    /* --------------------- Hiển thị bảng dòng (chỉ đọc) --------------------- */
    rewardModeName(mode) {
      return COMMISSION_ADHOC_REWARD_MODE_NAME[Number(mode)] || '';
    },
    itemTypeName(type) {
      return COMMISSION_ADHOC_ITEM_TYPE_NAME[Number(type)] || '';
    },
    /**
     * Rút gọn số tiền cho dễ đọc: 6.000.000 -> "6 triệu", 500.000 -> "500 nghìn".
     * Số lẻ giữ nguyên dạng đầy đủ để không làm sai lệch con số.
     */
    shortMoney(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) {
        return '';
      }
      if (numeric !== 0 && numeric % 1000000 === 0) {
        return `${(numeric / 1000000).toLocaleString('vi-VN')} triệu`;
      }
      if (numeric !== 0 && numeric % 1000 === 0) {
        return `${(numeric / 1000).toLocaleString('vi-VN')} nghìn`;
      }
      return `${numeric.toLocaleString('vi-VN')} đ`;
    },

    /**
     * Đọc điều kiện doanh thu thành câu tiếng Việt.
     *
     * Quy ước của engine (calculator.isRevenueInRange):
     *   vế trái  = amount1 <toán tử> DT   ⇒ "≤" nghĩa là DT TỪ amount1 trở lên
     *   vế phải  = DT <toán tử> amount2
     * Chiều này dễ nhầm nên phải diễn giải lại cho người nhập tự soát.
     *
     * @returns {{text: string, warn: boolean}}
     */
    describeRevenue(detail) {
      const OP = { NONE: 1, LT: 2, GT: 3, LTE: 4, GTE: 5 };
      const amount1 = this.toAmount(detail.amount1);
      const amount2 = this.toAmount(detail.amount2);
      const operator1 = Number(detail.amount1Operator);
      const operator2 = Number(detail.amount2Operator);
      const has1 = amount1 !== null && operator1 !== OP.NONE;
      const has2 = amount2 !== null && operator2 !== OP.NONE;

      if (!has1 && !has2) {
        return { text: 'Áp dụng cho mọi mức doanh thu', warn: false };
      }

      const lowIsFloor = operator1 === OP.LTE || operator1 === OP.LT;
      const highIsCeil = operator2 === OP.LTE || operator2 === OP.LT;

      if (has1 && has2 && lowIsFloor && highIsCeil) {
        // Cận dưới lớn hơn cận trên -> không hoá đơn nào lọt, phải cảnh báo
        if (amount1 > amount2) {
          return {
            warn: true,
            text:
              `Khoảng không hợp lệ: cận dưới (${this.shortMoney(amount1)}) lớn hơn ` +
              `cận trên (${this.shortMoney(amount2)}) — sẽ không có hoá đơn nào đạt`,
          };
        }
        const from =
          operator1 === OP.LTE
            ? `từ ${this.shortMoney(amount1)}`
            : `trên ${this.shortMoney(amount1)}`;
        const to =
          operator2 === OP.LTE
            ? `đến ${this.shortMoney(amount2)}`
            : `dưới ${this.shortMoney(amount2)}`;
        return { text: `Doanh thu ${from} ${to}`, warn: false };
      }

      const parts = [];
      if (has1) {
        if (operator1 === OP.LTE) {
          parts.push(`Doanh thu từ ${this.shortMoney(amount1)} trở lên`);
        } else if (operator1 === OP.LT) {
          parts.push(`Doanh thu lớn hơn ${this.shortMoney(amount1)}`);
        } else if (operator1 === OP.GTE) {
          parts.push(`Doanh thu tối đa ${this.shortMoney(amount1)}`);
        } else if (operator1 === OP.GT) {
          parts.push(`Doanh thu nhỏ hơn ${this.shortMoney(amount1)}`);
        }
      }
      if (has2) {
        if (operator2 === OP.LTE) {
          parts.push(`Doanh thu tối đa ${this.shortMoney(amount2)}`);
        } else if (operator2 === OP.LT) {
          parts.push(`Doanh thu nhỏ hơn ${this.shortMoney(amount2)}`);
        } else if (operator2 === OP.GTE) {
          parts.push(`Doanh thu từ ${this.shortMoney(amount2)} trở lên`);
        } else if (operator2 === OP.GT) {
          parts.push(`Doanh thu lớn hơn ${this.shortMoney(amount2)}`);
        }
      }
      return { text: parts.join(' và '), warn: false };
    },

    /** Bảng dòng đã thêm dùng LẠI đúng câu mô tả của preview (NT-3) */
    describeAmount(detail) {
      return this.describeRevenue(detail).text;
    },
    /** Danh sách mức thưởng > 0, kèm tên vị trí */
    describeRates(detail) {
      return Object.keys(detail.rates)
        .map((jobTitleId) => {
          const amount = this.toAmount(detail.rates[jobTitleId]);
          const job = this.jobTitles.find((row) => Number(row.id) === Number(jobTitleId));
          return {
            jobTitleId,
            name: job ? job.name : `#${jobTitleId}`,
            amount: this.formatMoney(amount),
          };
        })
        .filter((row) => row.amount !== '0');
    },
    formatMoney(value) {
      const numeric = this.toAmount(value);
      return numeric === null ? '' : Number(numeric).toLocaleString('vi-VN');
    },

    /* ------------------- Tìm sản phẩm / danh mục (dùng chung) ---------------- */
    // Dùng LẠI đúng API mà nhóm hoa hồng đang dùng: một endpoint tra được cả
    // sản phẩm lẫn danh mục, phân biệt bằng typeSearch.
    onSearchItem(text) {
      this.detailForm.name = text;
      if (!text) {
        this.detailForm.referenceId = null;
      }
      this.debounceSearchItem(text, this.detailForm.type);
    },
    debounceSearchItem: debounce(function (text, type) {
      if (!text) {
        this.itemSuggestions = [];
        return;
      }
      const typeSearch =
        Number(type) === COMMISSION_ADHOC_ITEM_TYPE.CATEGORY
          ? COMMISSION_SEARCH_TYPE.CATEGORY
          : COMMISSION_SEARCH_TYPE.PRODUCT;
      ApiService.setHeader();
      ApiService.get(
        `productSearch/search-by-product-category?searchProduct=${encodeURIComponent(
          text,
        )}&typeSearch=${typeSearch}&typeOfProduct=0`,
      )
        .then(({ data }) => {
          this.itemSuggestions = (data.data || []).map((item) => ({
            id: item.id,
            name: item.name || item.productName,
            suggestionName: item.code
              ? `${item.code} - ${item.name || item.productName}`
              : item.name || item.productName,
          }));
        })
        .catch(() => {
          this.itemSuggestions = [];
        });
    }, TIME_TRIGGER),
    onSelectItem(option) {
      this.detailForm.referenceId = option.item.id;
      this.detailForm.name = option.item.name;
    },

    /* ------------------------------- Lưu ----------------------------------- */
    buildPayload() {
      // API nhận danh sách phẳng [{storeId, scope}] — gộp hai kênh lại
      const stores = [];
      if (!this.model.applyAllRetail) {
        this.model.retailStores.forEach((store) => {
          stores.push({ storeId: store.id, scope: COMMISSION_STORE_SCOPE.RETAIL });
        });
      }
      if (!this.model.applyAllWholesale) {
        this.model.wholesaleStores.forEach((store) => {
          stores.push({ storeId: store.id, scope: COMMISSION_STORE_SCOPE.WHOLESALE });
        });
      }

      return {
        name: (this.model.name || '').trim(),
        description: this.model.description,
        startDate: this.toApiDate(this.model.startDate),
        endDate: this.toApiDate(this.model.endDate),
        status: this.model.status,
        applyAllRetailStores: this.model.applyAllRetail ? 1 : 0,
        applyAllWholesaleStores: this.model.applyAllWholesale ? 1 : 0,
        stores,
        details: this.model.details.map((detail, index) => ({
          type: detail.type,
          referenceId: detail.referenceId,
          amount1: this.toAmount(detail.amount1),
          amount1Operator: detail.amount1Operator,
          amount2: this.toAmount(detail.amount2),
          amount2Operator: detail.amount2Operator,
          conditionLogic: detail.conditionLogic,
          rewardMode: detail.rewardMode,
          conditionCategoryIds: (detail.conditionCategories || []).map((c) => c.id),
          sortOrder: index,
          employeeRates: Object.keys(detail.rates)
            .map((jobTitleId) => ({
              jobTitleId: Number(jobTitleId),
              amount: this.toAmount(detail.rates[jobTitleId]) || 0,
            }))
            .filter((rate) => rate.amount > 0),
        })),
      };
    },
    /** Ô nhập tiền cho phép gõ dấu phân cách — bỏ hết ký tự không phải số */
    toAmount(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const numeric = Number(String(value).replace(/[^0-9.-]/g, ''));
      return Number.isNaN(numeric) ? null : numeric;
    },
    save() {
      const payload = this.buildPayload();

      if (!payload.name) {
        return makeToastFaile('Vui lòng nhập tên chương trình');
      }
      if (!payload.startDate) {
        return makeToastFaile('Vui lòng chọn ngày bắt đầu');
      }
      if (
        !payload.applyAllRetailStores &&
        !payload.applyAllWholesaleStores &&
        payload.stores.length === 0
      ) {
        return makeToastFaile('Vui lòng chọn cửa hàng áp dụng');
      }
      if (payload.details.length === 0) {
        return makeToastFaile('Vui lòng thêm ít nhất một dòng chi tiết');
      }
      const missing = payload.details.findIndex((row) => !row.referenceId);
      if (missing > -1) {
        return makeToastFaile(`Dòng ${missing + 1}: chưa chọn sản phẩm/danh mục`);
      }
      const noRate = payload.details.findIndex((row) => row.employeeRates.length === 0);
      if (noRate > -1) {
        return makeToastFaile(
          `Dòng ${noRate + 1}: chưa nhập mức thưởng cho vị trí nào`,
        );
      }

      this.isSaving = true;
      ApiService.setHeader();
      const request = this.adhocId
        ? ApiService.put(`commission-adhocs/${this.adhocId}`, payload)
        : ApiService.post('commission-adhocs', payload);

      request
        .then(({ data }) => {
          makeToastSuccess(data.message);
          this.goBack();
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không lưu được chương trình'),
          );
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    goBack() {
      this.$router.push({ name: 'commission-adhocs' });
    },
  },
};
</script>

<style scoped>
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #181c32;
  padding-bottom: 8px;
  margin-bottom: 16px;
  border-bottom: 1px solid #ebedf3;
}
.detail-hint {
  font-size: 13px;
  border: 1px dashed #d6d9e0;
}
.detail-card {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  overflow: hidden;
}
.detail-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f3f6f9;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 13px;
  color: #181c32;
}
.detail-card__body {
  padding: 16px;
}
/* ------------------------------ Modal thêm dòng ----------------------------- */
/* Mỗi bước một khối có gạch phân cách, trừ khối cuối */
.form-block {
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #ebedf3;
}
.form-block--last {
  padding-bottom: 0;
  margin-bottom: 0;
  border-bottom: none;
}
.form-block__title {
  font-size: 13px;
  font-weight: 600;
  color: #181c32;
  margin-bottom: 12px;
}
.form-label {
  display: block;
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 4px;
}

/* Dải điều kiện doanh thu — đọc liền như một bất phương trình */
.range-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.range-row__amount {
  flex: 1 1 0;
  min-width: 0;
  text-align: right;
}
.range-row__operator {
  flex: 0 0 88px;
  width: 88px;
}
.range-row__label {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #181c32;
  padding: 0 4px;
}

/* Dòng đọc lại điều kiện — xanh khi hợp lệ, vàng khi cấu hình vô nghĩa */
.mode-hint {
  font-size: 12px;
  color: #7e8299;
  background: #f8f9fb;
  border: 1px solid #ebedf3;
  border-radius: 4px;
  padding: 8px 12px;
  margin-top: 20px;
  line-height: 1.6;
}
.range-preview {
  font-size: 13px;
  font-weight: 500;
  color: #187de4;
  background: #f1faff;
  border: 1px solid #cfe7fb;
  border-radius: 4px;
  padding: 8px 12px;
}
.range-preview--warn {
  color: #b8860b;
  background: #fff8e6;
  border-color: #ffe2a8;
}

/* Lưới mức thưởng: các ô đều nhau, tự xuống dòng theo bề rộng modal */
.rate-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 12px;
}
.rate-cell__label {
  display: block;
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Màn hẹp: dải điều kiện xuống dòng thay vì bị bóp méo */
@media (max-width: 575.98px) {
  .range-row {
    flex-wrap: wrap;
  }
  .range-row__amount {
    flex: 1 1 40%;
  }
  .range-row__label {
    width: 100%;
    text-align: center;
    padding: 4px 0;
  }
}
</style>
